import { createApi } from '@reduxjs/toolkit/query/react';
import qs from 'qs';

import { axiosBaseQuery } from './axios-base-query';

import { IS_SIGNED_IN } from '~/constants';

// Passport localstrategy requires the login payload to be in this format
interface LoginRequest {
  username: string;
  password: string;
  otpToken?: string;
  recoveryCode?: string;
}

interface GoogleLoginRequest {
  userInfo: any;
  access_token: string;
}

interface SignUpRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  organizationName?: string;
  jobTitle: string;
  invitationToken?: string;
  termsAccepted: boolean;
}

interface SignUpResponse {
  success: boolean;
  message: string;
  user: {
    id: string;
    auth_uid: string;
    email_verified: boolean;
    is_active: boolean;
    user_email: string;
    first_name: string;
    last_name: string;
    orgId: string;
    token?: string;
  };
}

interface VerificationRequest {
  emailVerifiedId: string;
}
interface VerificationResponse {
  user: UserT;
  recoveryCodes: string[];
}

interface AccountActivationRequest {
  activationId: string;
  activationType: boolean;
}
interface EmailResetRequest {
  resetEmail: string;
}

interface CreatePasswordRequest {
  newPassword: string;
  resetPasswordId: string;
}
interface OtpRequest {
  email: string;
}
interface OtpVerifyRequest {
  otpToken: number;
  email: string;
}
interface OtpDisableRequest {
  email: string;
  otpToken?: string;
  recoveryCode?: string;
}
interface OtpGenerateResponse {
  url: string;
  textCode: string;
  recoveryCodes: string[];
}

interface RequestPasswordResetRequest {
  currentPassword: string;
  newPassword: string;
}

interface ConfirmPasswordResetRequest {
  resetPasswordId: string;
  newPassword?: string;
}
interface InvitationVerificationRequest {
  token: string;
}

interface InvitationVerificationResponse {
  valid: boolean;
  invitation?: {
    _id: string;
    email: string;
    organizationId: string;
    role: string;
    status: string;
    expiresAt: string;
    organization: {
      _id: string;
      name: string;
      hasSso?: boolean;
    };
    inviter: {
      _id: string;
      firstName: string;
      lastName: string;
    };
  };
  error?: string;
}

interface AcceptInvitationRequest {
  token: string;
}

interface AcceptInvitationResponse {
  success: boolean;
  message: string;
  membership?: {
    _id: string;
    role: string;
    status: string;
    organization: {
      _id: string;
      name: string;
    };
  };
}

interface MicrosoftLoginRequest {
  idToken: string;
  otpToken?: string;
  recoveryCode?: string;
}

interface MicrosoftLoginResponse {
  success: boolean;
  requires2FA?: boolean;
  user: UserT;
  message?: string;
}

interface MicrosoftConfigResponse {
  clientId: string;
  tenantId: string;
  authority: string;
  redirectUri?: string;
  mode?: string;
}

interface OktaLoginRequest {
  idToken: string;
  otpToken?: string;
  recoveryCode?: string;
}

interface OktaLoginResponse {
  success: boolean;
  requires2FA?: boolean;
  user: UserT;
  message?: string;
}

interface OktaConfigResponse {
  clientId: string;
  issuer: string;
  domain: string;
}

interface SsoProvider {
  type: 'okta' | 'microsoft';
  config?: Record<string, string>;
}

interface SsoDiscoverResponse {
  providers: SsoProvider[];
}

interface CheckEmailResponse {
  exists: boolean;
  emailVerified: boolean;
}

interface GenericResponse {
  message: string;
}
export const nodeAuthApi = createApi({
  reducerPath: 'nodeAuthApi',
  baseQuery: axiosBaseQuery({ baseUrl: '/auth' }),
  tagTypes: ['user'],
  endpoints: (builder) => ({
    // Get current authenticated user
    currentUser: builder.query<UserT | null, void>({
      providesTags: ['user'],
      query: () => ({
        url: '/user',
        method: 'get',
      }),
    }),
    // Login request
    login: builder.mutation<UserT, LoginRequest>({
      invalidatesTags: ['user'],
      query: (credentials) => ({
        url: '/login',
        method: 'post',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        data: qs.stringify(credentials),
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          if (typeof window !== 'undefined') {
            window.localStorage.setItem(IS_SIGNED_IN, 'true');
          }
        } catch {
          // Login failed; do not set IS_SIGNED_IN
        }
      },
    }),
    // Google login request
    googleLogin: builder.mutation<UserT, GoogleLoginRequest>({
      invalidatesTags: ['user'],
      query: (credentials) => ({
        url: '/google-login',
        method: 'post',
        headers: {
          'Content-Type': 'application/json',
        },
        data: credentials,
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          if (typeof window !== 'undefined') {
            window.localStorage.setItem(IS_SIGNED_IN, 'true');
          }
        } catch {
          // Login failed
        }
      },
    }),
    // Microsoft login request
    microsoftLogin: builder.mutation<
      MicrosoftLoginResponse,
      MicrosoftLoginRequest
    >({
      invalidatesTags: ['user'],
      query: (credentials) => ({
        url: '/microsoft/token',
        method: 'post',
        headers: {
          'Content-Type': 'application/json',
        },
        data: credentials,
      }),
    }),
    microsoftLoginWithInvitation: builder.mutation<
      MicrosoftLoginResponse,
      MicrosoftLoginRequest & { invitationToken: string }
    >({
      invalidatesTags: ['user'],
      query: (credentials) => ({
        url: '/microsoft/token-with-invitation',
        method: 'post',
        headers: {
          'Content-Type': 'application/json',
        },
        data: credentials,
      }),
    }),
    // Okta login request
    oktaLogin: builder.mutation<OktaLoginResponse, OktaLoginRequest>({
      invalidatesTags: ['user'],
      query: (credentials) => ({
        url: '/okta/token',
        method: 'post',
        headers: {
          'Content-Type': 'application/json',
        },
        data: credentials,
      }),
    }),
    oktaLoginWithInvitation: builder.mutation<
      OktaLoginResponse,
      OktaLoginRequest & { invitationToken: string }
    >({
      invalidatesTags: ['user'],
      query: (credentials) => ({
        url: '/okta/token-with-invitation',
        method: 'post',
        headers: {
          'Content-Type': 'application/json',
        },
        data: credentials,
      }),
    }),
    // Magic link login request
    magicLogin: builder.mutation<UserT, { token: string }>({
      invalidatesTags: ['user'],
      query: ({ token }) => ({
        url: `/magic-login/${token}`,
        method: 'post',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          if (typeof window !== 'undefined') {
            window.localStorage.setItem(IS_SIGNED_IN, 'true');
          }
        } catch {
          // Login failed
        }
      },
    }),
    // Logout request
    logout: builder.mutation<void, void>({
      query: () => ({
        url: '/logout',
        method: 'post',
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;

          // Clearing currentUser query without incurring another network call
          dispatch(
            nodeAuthApi.util.updateQueryData(
              'currentUser',
              undefined,
              (draft) => null
            )
          );

          // This will also remove the IS_SIGNED_IN flag in localstorage
          // And trigger all other browser tabs to perform logoff operation
          // (see hooks/user-profile.ts on how side effects are triggered)
          const themeMode = localStorage.getItem('theme-mode');
          localStorage.clear();
          if (themeMode) {
            localStorage.setItem('theme-mode', themeMode);
          }

          // Some attempts to reset the redux store without having the reload browser window
          // Not sure why resetApiState causes the loading state to be true
          // thunkApi.dispatch(userProfileApi.util.resetApiState());

          // Taking the easy way out.
          // reloading browser window forces the redux store to be cleared
          window.location.reload();
        } catch (err) {
          // Error can be found in network tab and RTK middleware
        }
      },
    }),
    // Sign up request
    signup: builder.mutation<SignUpResponse, SignUpRequest>({
      invalidatesTags: ['user'],
      query: (input) => ({
        url: '/signup',
        method: 'post',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        data: qs.stringify(input),
      }),
    }),
    // email verification request
    emailVerify: builder.mutation<void, VerificationRequest>({
      invalidatesTags: ['user'],
      query: (code) => ({
        url: '/user-email-verification',
        method: 'post',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        data: qs.stringify(code),
      }),
    }),
    // account activation request
    accountActivation: builder.mutation<void, AccountActivationRequest>({
      invalidatesTags: ['user'],
      query: (code) => ({
        url: '/user-account-activation',
        method: 'post',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        data: qs.stringify(code),
      }),
    }),
    // forgot password request
    forgotPassword: builder.mutation<void, EmailResetRequest>({
      invalidatesTags: ['user'],
      query: (code) => ({
        url: '/forgotPassword',
        method: 'post',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        data: qs.stringify(code),
      }),
    }),
    // create password request
    createPassword: builder.mutation<void, CreatePasswordRequest>({
      invalidatesTags: ['user'],
      query: (credential) => ({
        url: '/createNewPassword',
        method: 'post',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        data: qs.stringify(credential),
      }),
    }),
    changePassword: builder.mutation<
      GenericResponse,
      RequestPasswordResetRequest
    >({
      query: (data) => ({
        url: '/changePassword',
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        data: qs.stringify(data),
      }),
    }),
    requestPasswordReset: builder.mutation<
      GenericResponse,
      RequestPasswordResetRequest
    >({
      query: (data) => ({
        url: '/requestPasswordReset',
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        data: qs.stringify(data),
      }),
    }),
    confirmPasswordReset: builder.mutation<
      GenericResponse,
      ConfirmPasswordResetRequest
    >({
      query: (data) => ({
        url: `/confirm-reset-password/${data.resetPasswordId}`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        data: qs.stringify(data),
      }),
    }),
    invitationVerification: builder.query<
      InvitationVerificationResponse,
      InvitationVerificationRequest
    >({
      query: ({ token }) => ({
        url: `/invitation/${token}/verify`,
        method: 'get',
      }),
    }),

    acceptInvitation: builder.mutation<
      AcceptInvitationResponse,
      AcceptInvitationRequest
    >({
      query: ({ token }) => ({
        url: '/accept-invitation',
        method: 'post',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        data: qs.stringify({ token }),
      }),
    }),
    // Fetch Microsoft Entra ID SSO configuration
    microsoftConfig: builder.query<MicrosoftConfigResponse, void>({
      query: () => ({
        url: '/microsoft/config',
        method: 'get',
      }),
    }),
    // Fetch Okta SSO configuration
    oktaConfig: builder.query<OktaConfigResponse, void>({
      query: () => ({
        url: '/okta/config',
        method: 'get',
      }),
    }),

    // SSO discovery by email domain
    ssoDiscover: builder.query<SsoDiscoverResponse, string>({
      query: (email) => ({
        url: `/sso/discover?email=${encodeURIComponent(email)}`,
        method: 'get',
      }),
    }),

    checkEmailExists: builder.query<CheckEmailResponse, string>({
      query: (email) => ({
        url: `/check-email/${encodeURIComponent(email)}`,
        method: 'get',
      }),
    }),
  }),
});
